/**
 * WebSocket tick-processing Redux-Saga middleware.
 *
 * Listens for `"UPDATE_SCRIPS"` actions (dispatched by `websocket-handler.ts`
 * every 100 ms with a batch of ticks) and:
 * 1. Delegates to `updateScripsReducer` to update live LTP/volume/OI in the
 *    watchlist slice.
 * 2. For any symbols currently plotted in the chart, converts each tick into
 *    a `OneMinuteBar` via `ChartDataHandler.processTick` and dispatches a
 *    single `batchUpdateOneMinuteBars` action to minimise re-renders.
 * 3. Throttles AmiBroker IPC forwarding to at most once every 500 ms.
 *
 * ### Tick format
 * `payloadBody` is `string[][]`.  Each inner array is
 * `[scripId, timestamp, ltq, volume, oi, ltp]` — indexes match the
 * `TICK_*` constants exported from `websocket-handler.ts`.
 * Arrays are consumed as-is (no object allocation in the hot path).
 * The only place they are converted to objects is when forwarding to
 * AmiBroker, which the Tauri IPC layer requires typed data.
 *
 * The `idToSymbol` lookup cache is rebuilt only when the scrips list changes.
 */
import { takeEvery, put, call, select } from "redux-saga/effects";

import { updateScripsReducer } from "@store/watchlist-slice";
import { batchUpdateOneMinuteBars } from "@store/chart-slice";
import { chartDataHandler } from "@service/chart-data-handler";
import { TickData, OneMinuteBar } from "../types/chart.types";
import { pushTickDataToAmibroker } from "@service/ipc-handler";
import {
  TICK_SCRIP_ID,
  TICK_TS,
  TICK_VOLUME,
  TICK_OI,
  TICK_LTP,
} from "@service/websocket-handler";

// Cache the idToSymbol lookup to avoid rebuilding it on every tick batch.
let cachedIdToSymbol: Record<number, string> = {};
let cachedScripsRef: any[] | null = null;

export function* updateScripsSaga({ payload: { payloadBody }, type }) {
  yield put(updateScripsReducer(payloadBody));

  // Process chart data for active chart symbols
  try {
    const chartState = yield select((state) => state.chart);
    const activeSymbols = chartState?.activeSymbols || [];

    if (activeSymbols.length > 0 && Array.isArray(payloadBody)) {
      const activeSymbolSet = new Set(activeSymbols);
      // Rebuild idToSymbol lookup only when the scrips list changes.
      const watchlistScrips: any[] = yield select(
        (state) => state.watchlist?.scrips || []
      );
      if (watchlistScrips !== cachedScripsRef) {
        cachedScripsRef = watchlistScrips;
        cachedIdToSymbol = {};
        watchlistScrips.forEach((scrip: any) => {
          cachedIdToSymbol[scrip.id] = scrip.symbol;
        });
      }

      // Collect chart bars and dispatch once (batched) to avoid per-tick re-renders.
      const batchedBars: { symbol: string; bar: OneMinuteBar }[] = [];

      // payloadBody is string[][] — access by index, no object allocation.
      for (const tick of payloadBody as string[][]) {
        const scripId    = parseInt(tick[TICK_SCRIP_ID], 10);
        const symbolName = cachedIdToSymbol[scripId];
        if (symbolName && activeSymbolSet.has(symbolName)) {
          const tickData: TickData = {
            symbol: symbolName,
            ltp:       parseFloat(tick[TICK_LTP]),
            volume:    parseFloat(tick[TICK_VOLUME]) || 0,
            oi:        parseFloat(tick[TICK_OI])     || 0,
            timestamp: new Date(tick[TICK_TS]).getTime(),
          };
          const bar = chartDataHandler.processTick(tickData);
          if (bar) batchedBars.push({ symbol: symbolName, bar });
        }
      }

      if (batchedBars.length > 0) {
        yield put(batchUpdateOneMinuteBars(batchedBars));
      }
    }
  } catch (error) {
    console.error("Error processing chart data:", error);
  }

  // Forward ticks to AmiBroker (throttled to 2×/second).
  // AmiBroker only needs the latest snapshot per symbol — more frequent
  // sends waste IPC bandwidth without adding value.
  // This is the ONLY place array rows are converted to objects.
  try {
    const amiInitialized: boolean = yield select(
      (state: any) => state.amibroker?.isInitialized || false
    );
    if (amiInitialized && Array.isArray(payloadBody) && payloadBody.length > 0) {
        // Convert to the object shape that the Tauri Rust handler expects.
        const amiTicks = (payloadBody as string[][]).map((tick) => ({
          scripId:   parseInt(tick[TICK_SCRIP_ID], 10),
          timestamp: tick[TICK_TS],
          ltp:       parseFloat(tick[TICK_LTP]),
          volume:    parseFloat(tick[TICK_VOLUME]),
          oi:        parseFloat(tick[TICK_OI]),
        }));
        pushTickDataToAmibroker(amiTicks);
      }
  } catch {
    // Tick forwarding errors are silent
  }
}

export function* websocketWatcher() {
  yield takeEvery("UPDATE_SCRIPS", updateScripsSaga);
}
